import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {
  GridColDef,
  GridOverlay,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { levelReportedValue } from "../models/fuelCellMetrics";
import { FuelCellRow, FuelCellSample, LOW_FUEL_THRESHOLD_PERCENT } from "../models/FuelCellRow";
import { FuelCellRowDetail } from "./FuelCellRowDetail";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { formatNumber } from "@/utilities/formatNumber";
import { safeLocale } from "@/utilities/i18n/safeLocale";

interface Props {
  rows: FuelCellRow[];
  samplesByDevice: Map<string, FuelCellSample[]>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  fromDate: Date;
  toDate: Date;
}

/**
 * Hidden by default but always part of the exported file: a sheet passed on needs the vehicle
 * identifiers and the flag that greys out untrustworthy tank figures.
 */
const FORCED_EXPORT_FIELDS = ["licensePlate", "vin", "isStale"];

const FRACTION_DIGITS: Record<string, number> = {
  tankCapacityKg: 2,
  levelPercent: 0,
  remainingKg: 2,
  usedKg: 2,
  usedRangeKg: 2,
  refillCount: 0,
  distanceKm: 0,
  avgConsumptionKgPer100Km: 2,
};

function NoDataOverlay() {
  const { t } = useTranslation();
  return (
    <GridOverlay>
      <Typography color="text.secondary">
        {t("fuelCell.noDevices", "No fuel cell devices found")}
      </Typography>
    </GridOverlay>
  );
}

/** Shown instead of the empty-table text when loading failed, so an error never reads as "no vehicles". */
function LoadFailedOverlay() {
  const { t } = useTranslation();
  return (
    <GridOverlay>
      <Typography color="error">
        {t("fuelCell.loadFailed")}
      </Typography>
    </GridOverlay>
  );
}

/**
 * A measurement as a bare number.
 *
 * The unit belongs in the header, which already carries it on every one of these columns. Put in
 * the cell as well it is both repeated on screen and, more to the point, turns the column into
 * text that cannot be summed or charted once exported.
 */
function formatMeasurement(value: number | null | undefined, language: string, precision = 2) {
  return formatNumber(value, language, { precision, missing: "" });
}

/**
 * A tank figure, greyed out where the vehicle drove without reporting its level.
 *
 * The number is still shown rather than blanked: it is the last reading there was, and a reader
 * comparing vehicles is better served by a visibly doubtful figure than by an empty cell.
 */
function TankValueCell({
  value,
  row,
  language,
}: GridRenderCellParams<FuelCellRow, number | null> & { language: string }) {
  return (
    <Typography variant="body2" sx={{ color: row.isStale ? "text.disabled" : "text.primary" }}>
      {formatMeasurement(value, language)}
    </Typography>
  );
}

function LevelBarCell({
  value,
  row,
  language,
}: GridRenderCellParams<FuelCellRow, number | null> & { language: string }) {
  const theme = useTheme();
  if (value == null) return null;
  const clamped = Math.max(0, Math.min(100, value));
  // Grey rather than red, amber or green where the vehicle drove without reporting its level:
  // a bar coloured by a level nobody stands behind is the one reading worse than no bar at all.
  const color = row.isStale
    ? theme.palette.text.disabled
    : clamped < LOW_FUEL_THRESHOLD_PERCENT
      ? theme.palette.error.main
      : clamped < 40
        ? theme.palette.warning.main
        : theme.palette.success.main;
  return (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 1,
          bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.25 : 0.15),
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: `${clamped}%`, height: "100%", bgcolor: color }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 42, textAlign: "right" }}>
        {formatNumber(clamped, language, { missing: "" })} %
      </Typography>
    </Box>
  );
}

export function FuelCellGrid({
  rows,
  samplesByDevice,
  isLoading,
  isFetching,
  isError,
  fromDate,
  toDate,
}: Props) {
  const { t, i18n } = useTranslation();

  const columns: GridColDef<FuelCellRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t("fuelCell.name", "Vehicle"),
        flex: 1.4,
        minWidth: 160,
      },
      {
        field: "licensePlate",
        headerName: t("fuelCell.licensePlate", "License plate"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "vin",
        headerName: t("vin", "VIN"),
        flex: 1.2,
        minWidth: 160,
      },
      {
        field: "tankCapacityKg",
        headerName: t("fuelCell.tankCapacityKg", "Tank capacity (kg)"),
        type: "number",
        flex: 1,
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatMeasurement(value, i18n.language),
      },
      {
        field: "levelPercent",
        headerName: t("fuelCell.levelPercent"),
        type: "number",
        flex: 1.2,
        minWidth: 160,
        align: "right",
        headerAlign: "right",
        renderCell: params => <LevelBarCell {...params} language={i18n.language} />,
      },
      {
        field: "remainingKg",
        headerName: t("fuelCell.remainingKg", "Remaining (kg)"),
        type: "number",
        flex: 1,
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatMeasurement(value, i18n.language),
        renderCell: params => <TankValueCell {...params} language={i18n.language} />,
      },
      {
        field: "usedKg",
        headerName: t("fuelCell.usedKg", "Used (kg)"),
        type: "number",
        flex: 1,
        minWidth: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatMeasurement(value, i18n.language),
        renderCell: params => <TankValueCell {...params} language={i18n.language} />,
      },
      {
        field: "usedRangeKg",
        headerName: t("fuelCell.usedRangeKg", "Used in range (kg)"),
        type: "number",
        flex: 1,
        minWidth: 150,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatMeasurement(value, i18n.language),
        renderCell: params => <TankValueCell {...params} language={i18n.language} />,
      },
      {
        field: "refillCount",
        headerName: t("fuelCell.refillCount", "Refills"),
        type: "number",
        flex: 0.7,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatMeasurement(value, i18n.language, 0),
      },
      {
        field: "distanceKm",
        headerName: t("fuelCell.distanceKm", "Distance (km)"),
        type: "number",
        flex: 1,
        minWidth: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatMeasurement(value, i18n.language, 0),
      },
      {
        field: "avgConsumptionKgPer100Km",
        headerName: t("fuelCell.avgConsumptionKgPer100Km", "Avg consumption (kg/100km)"),
        type: "number",
        flex: 1.1,
        minWidth: 170,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatMeasurement(value, i18n.language),
      },
      {
        field: "isStale",
        headerName: t("fuelCell.levelReported"),
        type: "boolean",
        flex: 0.8,
        minWidth: 150,
        // Inverted on purpose: the sheet should read as a statement about the data being there,
        // not as a flag whose meaning the reader has to look up.
        valueGetter: (_value, row) => levelReportedValue(row),
        valueFormatter: (value: boolean) => (value ? t("common.yes") : t("common.no")),
      },
      {
        field: "updatedAt",
        headerName: t("fuelCell.updatedAt", "Last update"),
        type: "dateTime",
        flex: 1.2,
        minWidth: 170,
        valueGetter: (value: Date | null) => (value ? new Date(value) : null),
        valueFormatter: (value: Date | null) =>
          value
            ? value.toLocaleString(safeLocale(i18n.language), {
                hour: "2-digit",
                minute: "2-digit",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
      },
    ],
    [t, i18n.language],
  );

  return (
    <Box sx={{ flex: 1, minHeight: 640, position: "relative" }}>
      {isFetching && !isLoading && (
        <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }} />
      )}
      <I18nGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        settingsKey="fuelCell"
        initialState={{
          density: "compact",
          sorting: { sortModel: [{ field: "name", sort: "asc" }] },
          // Both identify the vehicle its name already identifies, so they cost width without
          // adding anything. Still there for a reader who needs them, and in the export.
          columns: { columnVisibilityModel: { licensePlate: false, vin: false, isStale: false } },
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        getDetailPanelHeight={() => 320}
        getDetailPanelContent={({ row }) => (
          <FuelCellRowDetail row={row} samples={samplesByDevice.get(row.id) ?? []} />
        )}
        slots={{
          toolbar: ExportToolbar,
          noRowsOverlay: isError ? LoadFailedOverlay : NoDataOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress" as const,
            noRowsVariant: "skeleton" as const,
          },
          toolbar: {
            report: { title: t("fuelCell.title"), period: { from: fromDate, to: toDate } },
            fractionDigits: FRACTION_DIGITS,
            exportDisabled: isLoading || isError,
            exportForcedFields: FORCED_EXPORT_FIELDS,
          },
        }}
      />
    </Box>
  );
}
